import React, { useEffect, useMemo, useState } from 'react';
import { CertificateListEntry } from './CertificatesOverview';
import {
	AccessorKeyColumnDef,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	Table as __Table,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import useTableState from '../useTableState';
import { Table as BTable } from 'react-bootstrap';
import useEstablishments, { Establishment } from '../../../hooks/use-establishments';
import TableFooter from '../Components/TableFooter';
import TableBody from '../Components/TableBody';
import TableHeader from '../Components/TableHeader';

type Props = {
	data: CertificateListEntry[];
	columns: AccessorKeyColumnDef<CertificateListEntry, unknown>[];
	handleRowClick: (row: CertificateListEntry) => void;
	prepareDownload: () => void;
};
const tableName = 'certificatesList';
const Table = ({ data, columns, handleRowClick, prepareDownload }: Props) => {
	const { t } = useTranslation();

	const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<number | null>(null);
	const establishments: Establishment[] = useEstablishments();
	const { setPageIndex, setPageSize, pageIndex, pageSize } = useTableState(tableName);

	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex,
		pageSize,
	});

	const [sorting, setSorting] = React.useState<SortingState>([
		{
			id: 'certificateExpirationDate',
			desc: false,
		},
	]);

	const [globalFilter, setGlobalFilter] = React.useState<string>('');

	useEffect(() => {
		setPageIndex(pagination.pageIndex);
		setPageSize(pagination.pageSize);
	}, [pagination]);

	const filteredData = useMemo(() => {
		if ((selectedEstablishmentId || 0) > 0) {
			return data.filter(
				(certificateListEntry) => certificateListEntry.establishmentId === selectedEstablishmentId,
			);
		}

		return data;
	}, [data, selectedEstablishmentId]);

	const table = useReactTable({
		data: filteredData,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		enableGlobalFilter: true,
		onGlobalFilterChange: setGlobalFilter,
		state: {
			pagination,
			sorting,
			globalFilter,
		},
	});

	return (
		<>
			<div className="d-flex justify-content-between">
				<div>
					{establishments.length > 0 ? (
						<select
							className="form-select"
							onChange={({ target }) => setSelectedEstablishmentId(parseInt(target.value, 10))}
							style={{ minWidth: 250 }}
							value={selectedEstablishmentId || 0}>
							<option value={0}>{t('table.allEstablishments')}</option>
							{establishments.map((establishment) => (
								<option key={establishment.id} value={establishment.id}>
									{establishment.name}
								</option>
							))}
						</select>
					) : null}
				</div>
				<div className="d-flex flex-row align-items-center">
					<label className="me-2">{t('table.search')}: </label>
					<input
						className="form-control form-control-sm d-inline-block mt-1"
						value={globalFilter}
						onChange={(e) => {
							setGlobalFilter(e.target.value);
						}}
					/>
				</div>
			</div>
			<BTable responsive hover size={'l'}>
				<TableHeader table={table as __Table<unknown>} />
				<TableBody table={table as __Table<unknown>} handleRowClick={handleRowClick} />
			</BTable>
			<TableFooter
				table={table as __Table<unknown>}
				download={{
					prepareDownload,
					fileName: `${t('certificates.table.exportedFile')}.xlsx`,
					title: t('table.export'),
				}}
			/>
		</>
	);
};

export default Table;
